import type { PartnerNoti } from '@seek/indie-api-types';
import {
  isEmail as validateEmail,
  isPhone as validatePhone,
} from '@seek/validators-js';
import {
  Actions,
  Button,
  ButtonIcon,
  Checkbox,
  Dialog,
  IconInfo,
  Inline,
  Stack,
  Text,
  TextField,
  useToast,
} from 'braid-design-system';
import { useEffect, useState } from 'react';

import { useNotiConfig } from '../../../../../../ConfigContext.tsx';
import type { EmailAddress, PhoneNumber } from '../../MultiItemConfig.tsx';

export type ItemFormErrors = {
  updateOrAddItem: boolean;
  selectedOptions: boolean;
};

interface ItemDialogProps {
  action: 'add' | 'update';
  selectedItem: EmailAddress | PhoneNumber | null;
  newFormTitle: string;
  itemKey: 'emailAddresses' | 'phoneNumbers';
  showItemDialog: boolean;
  setShowItemDialog: React.Dispatch<React.SetStateAction<boolean>>;
  notiConfig: PartnerNoti.PartnerNotiConfig;
}

const notiConfigOptions: PartnerNoti.SourceType[] = [
  'webhooks:missing-events',
  'hirer-relationship:changed',
  'webhooks:failing-endpoint',
  's3:release-notes',
];

const notiConfigLabels: Record<PartnerNoti.SourceType, string> = {
  'webhooks:missing-events': 'Webhooks Missing Events',
  'hirer-relationship:changed': 'Hirer Relationship Changed',
  'webhooks:failing-endpoint': 'Webhooks Failing Endpoint',
  's3:release-notes': 'S3 Release Notes',
};

const notiConfigInfos: Record<PartnerNoti.SourceType, string> = {
  'webhooks:missing-events':
    'This notification alerts you if your webhook endpoint(s) have been failing to receive events for over 48 hours, requiring manual replay of those events.',
  'hirer-relationship:changed':
    'This notification alerts you of any relationship changes with your hirers.',
  'webhooks:failing-endpoint':
    'This notification alerts you if your webhook endpoint(s) have returned unsuccessful responses in the past 2 hours.',
  's3:release-notes':
    'This notification alerts you of any significant updates to the SEEK API and our product offerings.',
};

const notiMethods = {
  email: 'Email Address',
  number: 'Phone Number',
};

export const ItemDialog = ({
  action,
  selectedItem,
  newFormTitle,
  showItemDialog,
  setShowItemDialog,
  itemKey,
  notiConfig,
}: ItemDialogProps) => {
  const [selectedOptions, setSelectedOptions] = useState<
    Partial<Record<PartnerNoti.SourceType, boolean>>
  >({});
  const [itemLoading, setItemLoading] = useState(false);
  const [updateOrAddItem, setUpdateOrAddItem] = useState('');
  const [formErrors, setFormErrors] = useState<ItemFormErrors>({
    updateOrAddItem: false,
    selectedOptions: false,
  });
  const isUpdate = action === 'update';
  const isPhoneNumber = itemKey === 'phoneNumbers';
  const updateButtonLabel = itemLoading ? 'Updating' : 'Update';
  const saveButtonLabel = itemLoading ? 'Saving' : 'Save';

  const validateEmailOrPhone = () => {
    const isValidEmail =
      !isPhoneNumber &&
      validateEmail.test({
        value: updateOrAddItem,
        validatorProps: {},
        formValues: {},
      });
    const isValidPhone =
      isPhoneNumber &&
      validatePhone.test({
        value: updateOrAddItem,
        validatorProps: {},
        formValues: {},
      });

    const isValidInput = isPhoneNumber ? isValidPhone : isValidEmail;

    setFormErrors({
      ...formErrors,
      updateOrAddItem: !isValidInput,
    });

    return isValidInput;
  };

  const showToast = useToast();
  const { update, refresh } = useNotiConfig();

  useEffect(() => {
    if (isUpdate && selectedItem) {
      const itemValue =
        'email' in selectedItem ? selectedItem.email : selectedItem?.number;
      setUpdateOrAddItem(itemValue);
    }

    const initialSelectedOptions = (
      selectedItem?.enabledSources ??
      notiConfigOptions.map((option) => ({ source: option, enabled: true }))
    ).reduce((acc, { source, enabled }) => {
      acc[source] = enabled;
      return acc;
    }, {} as Record<PartnerNoti.SourceType, boolean>);

    setSelectedOptions(initialSelectedOptions);
  }, [isUpdate, isPhoneNumber, selectedItem, itemKey]);

  const handleCheckboxChange =
    (option: PartnerNoti.SourceType) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      setSelectedOptions((prev) => ({
        ...prev,
        [option]: checked,
      }));
    };

  const closeAndClearForm = () => {
    setShowItemDialog(false);
    setFormErrors({ updateOrAddItem: false, selectedOptions: false });
  };

  const validateForm = () => {
    const isInputValid = validateEmailOrPhone();
    const noSelectedOption = !Object.values(selectedOptions).some(
      (selected) => selected,
    );

    setFormErrors({
      updateOrAddItem: !isInputValid,
      selectedOptions: noSelectedOption,
    });

    return isInputValid && !noSelectedOption;
  };

  const updatedOrAddedItem = async () => {
    if (!validateForm()) {
      return;
    }

    setItemLoading(true);
    const updatedEnabledSources = Object.entries(selectedOptions).map(
      ([source, enabled]) => ({ source, enabled }),
    );

    const itemToUpdateOrAdd = isPhoneNumber
      ? { number: updateOrAddItem, enabledSources: updatedEnabledSources }
      : { email: updateOrAddItem, enabledSources: updatedEnabledSources };

    const updatedOrAddItemList =
      isUpdate && selectedItem
        ? notiConfig[itemKey]?.map((item) => {
            if ('email' in item && 'email' in selectedItem) {
              return item.email === selectedItem.email
                ? itemToUpdateOrAdd
                : item;
            }
            if ('number' in item && 'number' in selectedItem) {
              return item.number === selectedItem.number
                ? itemToUpdateOrAdd
                : item;
            }
          })
        : [...(notiConfig[itemKey] ?? []), itemToUpdateOrAdd];

    const updatedConfig = {
      ...notiConfig,
      enableSyndicationFeeds: Boolean(notiConfig.feedUrlSecret),
      [itemKey]: updatedOrAddItemList,
    };

    const error = await update(updatedConfig);
    const message = isUpdate
      ? `We couldn’t update ${updateOrAddItem}.`
      : `We couldn’t add ${updateOrAddItem} to the list.`;
    if (error) {
      showToast({
        tone: 'critical',
        message,
        description: String(error),
      });
    }
    await refresh();
    closeAndClearForm();
    setItemLoading(false);
  };

  return (
    <Dialog
      open={showItemDialog}
      onClose={closeAndClearForm}
      id="updateOrAddItemDetails"
      title={newFormTitle}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          updatedOrAddedItem();
        }}
      >
        <Stack space="medium">
          <TextField
            id="updatedOrAddItem"
            label={notiMethods[isPhoneNumber ? 'number' : 'email']}
            value={updateOrAddItem}
            onChange={(e) => setUpdateOrAddItem(e.currentTarget.value)}
            tone="caution"
            message={
              formErrors.updateOrAddItem
                ? `Invalid ${isPhoneNumber ? 'phone number' : 'email address'}`
                : undefined
            }
          />
          {notiConfigOptions.map((option) => (
            <Inline key={option} space="xsmall" alignY="center">
              <Checkbox
                key={option}
                checked={Boolean(selectedOptions[option])}
                id={option}
                onChange={handleCheckboxChange(option)}
                label={notiConfigLabels[option]}
              />
              <ButtonIcon
                variant="transparent"
                icon={<IconInfo />}
                label={notiConfigInfos[option]}
                id={option}
              />
            </Inline>
          ))}
          {formErrors.selectedOptions && (
            <Text tone="caution">Please select at least one option</Text>
          )}
          <Inline space="gutter" alignY="bottom">
            <Actions>
              <Button
                onClick={() => updatedOrAddedItem()}
                loading={itemLoading}
              >
                {isUpdate ? updateButtonLabel : saveButtonLabel}
              </Button>
              <Button onClick={closeAndClearForm} variant="transparent">
                Cancel
              </Button>
            </Actions>
          </Inline>
        </Stack>
      </form>
    </Dialog>
  );
};
